import {Injectable} from "@angular/core";

import {Logger, LoggingService} from "framework/services/logging.service";
import {Lookup, UtilsService} from "framework/services/utils.service";

@Injectable()
export class CacheService
{
    private readonly logger: Logger;

    private readonly store: ICacheStore;

    /**
     * Constructor
     */
    constructor(private logService: LoggingService,
                private loggingService: LoggingService)
    {
        this.logger = logService.getLogger(CacheService);

        this.store = new InMemoryCacheStore(loggingService);
    }

    /**
     * Perform startup logic.
     */
    init()
    {
        this.logger.info("Initializing Cache");
    }

    /**
     * Get an item from the store.
     * @param key
     */
    get(key: string): CacheItem
    {
        return this.store.get(key);
    }

    /**
     * Get all items in store.
     */
    getAll(filter?: string): CacheItem[]
    {
        return this.store.getAll(filter);
    }

    /**
     * Get the value as a specified type.
     * @param key
     * @param defaultValue
     */
    getValueAs<T>(key: string,
                  defaultValue?: T): T
    {
        let item  = this.get(key);
        let value = item?.getValue<T>();
        return value ?? defaultValue;
    }

    /**
     * Wrap an operation in a cache.
     * @param key
     */
    flush(key: string): void
    {
        this.store.remove(key);
    }

    /**
     * Set the item in to the store with the specified cache behavior.
     * @param key
     * @param value
     */
    set(key: string,
        value: any)
    {
        if (!value)
        {
            this.remove(key);
        }
        else
        {
            let item = new CacheItem(key, value);

            this.logger.debug(`Caching Item (${item.key})`);

            this.store.set(item);
        }
    }

    /**
     * Remove the specified item from the store.
     * @param key
     */
    remove(key: string)
    {
        this.store.remove(key);
    }

    /**
     * Remove all items from the store.
     */
    removeAll()
    {
        this.store.removeAll();
    }

    /**
     * True if an item is valid (not stale or dirty)
     * @param key
     */
    isValid(key: string): boolean
    {
        return this.get(key) != null;
    }

    /**
     * Get the value as a specified type.
     */
    static getValueAs<T>(item: CacheItem,
                         defaultValue: T): T
    {
        return item ? <T>item.getValue() : defaultValue;
    }
}

export interface ICacheStore
{
    get(key: string): CacheItem;

    getAll(filter?: string): CacheItem[];

    set(item: CacheItem): void;

    remove(key: string): void;

    removeAll(): void;
}

export class CacheItem
{
    private readonly valueJson: string;

    readonly cachedOn = new Date();

    lastAccessed: Date;

    constructor(readonly key: string,
                value: any)
    {
        this.lastAccessed = this.cachedOn;

        this.valueJson = JSON.stringify(value);
    }

    get size(): number
    {
        return this.valueJson.length;
    }

    public getValue<M>(): M
    {
        return JSON.parse(this.valueJson);
    }

    public getFixupValue<M>(classReference: IFixupPrototype<M>): M
    {
        const value = this.getValue<M>();
        classReference.fixupPrototype(value);
        return value;
    }
}

export interface IFixupPrototype<M>
{
    fixupPrototype(instance: M): void;
}

export class CacheItemBlock
{
    constructor(readonly store: Lookup<CacheItem>,
                readonly currentSize: number)
    {}
}

//--//

export interface InMemoryCacheStoreOptions
{
    block?: CacheItemBlock;
    cacheThreshold?: number;
    cacheMaxSize?: number;
}

export interface InMemoryCacheStoreStats
{
    entries: number;
    currentSize: number;
    cacheThreshold: number;
    cacheMaxSize: number;
}

class LruKeyLinkedListNode
{
    public previous: LruKeyLinkedListNode;
    public next: LruKeyLinkedListNode;

    constructor(readonly key: string)
    {}
}

class LruKeyLinkedList
{
    private m_head: LruKeyLinkedListNode;
    private m_tail: LruKeyLinkedListNode;

    prepend(node: LruKeyLinkedListNode): void
    {
        node.previous = null;
        node.next     = this.m_head;
        if (this.m_head) this.m_head.previous = node;
        this.m_head = node;
        if (!this.m_tail) this.m_tail = node;
    }

    append(node: LruKeyLinkedListNode): void
    {
        node.next     = null;
        node.previous = this.m_tail;
        if (this.m_tail) this.m_tail.next = node;
        this.m_tail = node;
        if (!this.m_head) this.m_head = node;
    }

    moveToHead(node: LruKeyLinkedListNode): void
    {
        if (!node || this.m_head === node) return;
        this.remove(node);
        this.prepend(node);
    }

    remove(node: LruKeyLinkedListNode): void
    {
        if (!node) return;

        if (node.previous)
        {
            node.previous.next = node.next;
        }
        else if (this.m_head === node)
        {
            this.m_head = node.next;
        }

        if (node.next)
        {
            node.next.previous = node.previous;
        }
        else if (this.m_tail === node)
        {
            this.m_tail = node.previous;
        }

        node.previous = null;
        node.next     = null;
    }

    removeTail(): LruKeyLinkedListNode
    {
        const tail = this.m_tail;
        if (!tail) return null;
        this.remove(tail);
        return tail;
    }

    clear(): void
    {
        this.m_head = null;
        this.m_tail = null;
    }
}

interface InMemoryCacheEntry
{
    item: CacheItem;
    size: number;
    node: LruKeyLinkedListNode;
}

export class InMemoryCacheStore implements ICacheStore
{
    private static readonly s_defaultCacheThreshold: number = 512 * 1024; // Maximum size of an item in the cache.
    private static readonly s_defaultCacheMaxSize: number   = 64 * 1024 * 1024; // Maximum size of the cache.

    private readonly m_cacheThreshold: number;
    private readonly m_cacheMaxSize: number;

    private m_store     = new Map<string, InMemoryCacheEntry>();
    private m_lruList   = new LruKeyLinkedList();
    private currentSize = 0;
    private readonly logger: Logger;

    constructor(loggingService: LoggingService,
                constructorOptions?: InMemoryCacheStoreOptions)
    {
        this.logger = loggingService.getLogger(InMemoryCacheStore);

        this.m_cacheThreshold = Math.max(0, constructorOptions?.cacheThreshold ?? InMemoryCacheStore.s_defaultCacheThreshold);
        this.m_cacheMaxSize   = Math.max(0, constructorOptions?.cacheMaxSize ?? InMemoryCacheStore.s_defaultCacheMaxSize);

        if (constructorOptions?.block?.store) this.restoreFromBlock(constructorOptions.block);
    }

    cacheBlock(leftoverSize: number): CacheItemBlock
    {
        leftoverSize   = Math.max(0, leftoverSize || 0);
        const maxSize  = Math.max(0, this.m_cacheThreshold - leftoverSize);
        const currSize = new CacheItem(null, this.snapshotBlock()).size;
        if (currSize > maxSize)
        {
            this.ageOffLeastAccessed(Math.floor(100 * maxSize / currSize));
        }

        return this.snapshotBlock();
    }

    has(key: string): boolean
    {
        return this.m_store.has(key);
    }

    stats(): InMemoryCacheStoreStats
    {
        return {
            entries       : this.m_store.size,
            currentSize   : this.currentSize,
            cacheThreshold: this.m_cacheThreshold,
            cacheMaxSize  : this.m_cacheMaxSize
        };
    }

    get(key: string): CacheItem
    {
        const entry = this.m_store.get(key);
        if (!entry) return null;

        this.touch(entry);
        return this.ensurePrototype(entry.item);
    }

    getAll(filter?: string): CacheItem[]
    {
        const result: CacheItem[] = [];
        for (const [key, entry] of this.m_store)
        {
            if (filter && !key.startsWith(filter)) continue;
            this.touch(entry);
            result.push(this.ensurePrototype(entry.item));
        }

        return result;
    }

    set(item: CacheItem)
    {
        if (!item?.key) return;

        this.remove(item.key);

        const size = item.size;
        if (size > this.m_cacheThreshold)
        {
            this.logger.debug(`Skipping caching item (${item.key}) due to size: ${size}`);
            return;
        }

        const entry: InMemoryCacheEntry = {
            item: this.ensurePrototype(item),
            size: size,
            node: new LruKeyLinkedListNode(item.key)
        };
        entry.item.lastAccessed         = new Date();
        this.m_store.set(item.key, entry);
        this.m_lruList.prepend(entry.node);
        this.currentSize += size;

        if (this.currentSize > this.m_cacheMaxSize)
        {
            this.ageOffLeastAccessed(80, "max-size");
        }
    }

    remove(key: string)
    {
        const entry = this.m_store.get(key);
        if (!entry) return;

        this.m_store.delete(key);
        this.m_lruList.remove(entry.node);
        this.currentSize = Math.max(0, this.currentSize - entry.size);
    }

    removeAll()
    {
        this.m_store.clear();
        this.m_lruList.clear();
        this.currentSize = 0;
    }

    private ageOffLeastAccessed(percentageToKeep: number,
                                reason: string = "manual")
    {
        const clamped    = UtilsService.clamp(0, 100, percentageToKeep || 0);
        const beforeSize = this.currentSize;
        const targetSize = Math.min(this.currentSize, this.m_cacheMaxSize) * clamped / 100;

        let removed = 0;
        while (this.currentSize > targetSize && this.removeTailEntry())
        {
            removed++;
        }

        this.logger.debug(`LRU trim reason=${reason} keepPercent=${clamped} removedItems=${removed} sizeBefore=${beforeSize} sizeAfter=${this.currentSize} targetSize=${targetSize} remainingItems=${this.m_store.size}`);
    }

    private removeTailEntry(): boolean
    {
        const node = this.m_lruList.removeTail();
        if (!node) return false;

        const entry = this.m_store.get(node.key);
        if (!entry) return true;

        this.m_store.delete(node.key);
        this.currentSize = Math.max(0, this.currentSize - entry.size);
        return true;
    }

    private touch(entry: InMemoryCacheEntry): void
    {
        entry.item.lastAccessed = new Date();
        this.m_lruList.moveToHead(entry.node);
    }

    private ensurePrototype(item: CacheItem): CacheItem
    {
        Object.setPrototypeOf(item, CacheItem.prototype);
        return item;
    }

    private snapshotBlock(): CacheItemBlock
    {
        const lookup: Lookup<CacheItem> = {};
        for (const [key, entry] of this.m_store)
        {
            lookup[key] = entry.item;
        }

        return new CacheItemBlock(lookup, this.currentSize);
    }

    private restoreFromBlock(block: CacheItemBlock): void
    {
        const items = Object.entries(block.store || {})
                            .map(([key, item]) => ({
                                key         : key,
                                item        : item,
                                lastAccessed: this.getLastAccessed(item)
                            }))
                            .filter((entry) => !!entry.key && !!entry.item)
                            .sort((a,
                                   b) => b.lastAccessed - a.lastAccessed); // most recently used first

        for (const restored of items)
        {
            const item = this.ensurePrototype(restored.item);
            const size = item.size;
            if (size > this.m_cacheThreshold) continue;

            const node = new LruKeyLinkedListNode(restored.key);
            this.m_store.set(restored.key,
                             {
                                 item: item,
                                 size: size,
                                 node: node
                             });
            this.m_lruList.append(node);
            this.currentSize += size;
        }

        if (this.currentSize > this.m_cacheMaxSize)
        {
            this.ageOffLeastAccessed(80, "restore-overflow");
        }
    }

    private getLastAccessed(item: CacheItem): number
    {
        if (!item) return 0;
        if (item.lastAccessed) return new Date(item.lastAccessed).getTime();
        if (item.cachedOn) return new Date(item.cachedOn).getTime();
        return 0;
    }
}

//--//

export abstract class WebStorageCacheStore implements ICacheStore
{
    constructor(private storage: Storage,
                private cachePrefix: string)
    {
    }

    private key(key: string): string
    {
        return this.cachePrefix + "_" + key;
    }

    get(key: string): CacheItem
    {
        let item = this.performGet(key);
        if (item)
        {
            item.lastAccessed = new Date();
            this.set(item);
        }

        return item;
    }

    private performGet(key: string): CacheItem
    {
        let itemJson = this.storage.getItem(this.key(key));
        if (itemJson)
        {
            return this.getValue(itemJson);
        }

        return null;
    }

    private getValue(json: string): CacheItem
    {
        const value = JSON.parse(json) as CacheItem;
        Object.setPrototypeOf(value, CacheItem.prototype);
        return value;
    }

    getAll(filter?: string): CacheItem[]
    {
        let result: CacheItem[] = [];

        for (let cacheKey of this.getAllKeys(filter))
        {
            let itemJson = this.storage.getItem(cacheKey);
            if (itemJson) result.push(this.getValue(itemJson));
        }

        return result;
    }

    private getAllKeys(filter?: string): string[]
    {
        let result: string[] = [];

        let prefix = this.key(filter || "");

        for (let i = 0; i < this.storage.length; i++)
        {
            let cacheKey = this.storage.key(i);
            if (cacheKey.startsWith(prefix))
            {
                result.push(cacheKey);
            }
        }

        return result;
    }

    set(item: CacheItem)
    {
        this.performSet(item.key, item, 0);
    }

    private performSet(key: string,
                       item: CacheItem,
                       attempt: number)
    {
        if (attempt < 5)
        {
            try
            {
                this.storage.setItem(this.key(key), JSON.stringify(item));
            }
            catch (e)
            {
                if (this.isQuotaExceeded(e))
                {
                    // age off the least accessed and reattempt performSet
                    this.ageOffLeastAccessed(15);
                    this.performSet(key, item, attempt + 1);
                }
            }
        }
    }

    private isQuotaExceeded(e: any): boolean
    {
        let quotaExceeded = false;

        if (e)
        {
            if (e.code)
            {
                switch (e.code)
                {
                    case 22:
                        quotaExceeded = true;
                        break;
                    case 1014:
                        // Firefox
                        if (e.name === "NS_ERROR_DOM_QUOTA_REACHED")
                        {
                            quotaExceeded = true;
                        }
                        break;
                }
            }
            else if (e.number === -2147024882)
            {
                // Internet Explorer 8
                quotaExceeded = true;
            }
        }

        return quotaExceeded;
    }

    private ageOffLeastAccessed(percentageToRemove: number)
    {
        // find all with session only or session data and order by last access
        let lastAccessedList = this.getAll()
                                   .sort((itemA,
                                          itemB) =>
                                         {
                                             return this.getLastAccessed(itemA) - this.getLastAccessed(itemB);
                                         });

        // clear X%
        if (lastAccessedList && lastAccessedList.length)
        {
            let countToRemove = Math.floor(lastAccessedList.length * percentageToRemove / 100);

            let removalCandidates = lastAccessedList.slice(0, countToRemove);

            for (let candidate of removalCandidates)
            {
                this.remove(candidate.key);
            }
        }
    }

    private getLastAccessed(item: CacheItem): number
    {
        if (item.lastAccessed) return new Date(item.lastAccessed).getTime();
        if (item.cachedOn) return new Date(item.cachedOn).getTime();
        return 0;
    }

    remove(key: string)
    {
        this.storage.removeItem(this.key(key));
    }

    removeAll()
    {
        for (let key of this.getAllKeys())
        {
            this.storage.removeItem(key);
        }
    }
}

export class LocalStorageCacheStore extends WebStorageCacheStore
{
    constructor(cachePrefix: string)
    {
        super(localStorage, cachePrefix);
    }
}

export class SessionStorageCacheStore extends WebStorageCacheStore
{
    constructor(cachePrefix: string)
    {
        super(sessionStorage, cachePrefix);
    }
}

export class DisabledCacheStore implements ICacheStore
{
    constructor(private cachePrefix: string)
    {
    }

    private key(key: string): string
    {
        return this.cachePrefix + "_" + key;
    }

    get(key: string): CacheItem
    {
        return null;
    }

    getAll(filter?: string): CacheItem[]
    {
        return [];
    }

    set(item: CacheItem)
    {
        // does nothing
    }

    remove(key: string)
    {
        // does nothing
    }

    removeAll()
    {
        // does nothing
    }
}
